import React, { useState, useEffect, useRef } from "react";
import Main from "../Main";
import AppBreadcrumbs from "../../components/ui/AppBreadcrumbs";
import { Autocomplete, FormControl, FormLabel, TextField } from "@mui/material";

import {
  fetch_fastag_info,
  fetch_fastag_operators,
  submit_fastag_amount,
} from "../../api/billPayApi";

import ServicesForm from "./ServiceForm";
import { getLocation } from "../../components/Navigator/Navigator";
import { useParams, useSearchParams } from "react-router-dom";
import { useInactivityTimeout } from "../../hooks/useInactivityTimeout";
import { useNotify } from "../../context/NotifyContext";

const Fastag = () => {
  const { notify } = useNotify();
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchedRef = useRef(null);

  const [reg, setReg] = useState("");
  const [operators, setOperators] = useState([]);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [selectedOperatorId, setSelectedOperatorId] = useState("");

  const [details, setDetails] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [getLoading, setGetLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  /* CLEAR SESSION */
  const clearSession = () => {
    fetchedRef.current = null;
    setSearchParams({}, { replace: true });
    setReg("");
    setSelectedOperator(null);
    setSelectedOperatorId("");
    setDetails(null);
    setShowDetails(false);
  };

  /* AUTO SESSION CLEAR */
  useInactivityTimeout(() => {
    if (showDetails) {
      clearSession();
      notify("Session expired", "info");
    }
  }, 10 * 60 * 1000);

  /* LOAD OPERATORS */
  useEffect(() => {
    const load = async () => {
      const res = await fetch_fastag_operators();
      if (res?.success) setOperators(res.data?.operators || []);
    };
    load();
  }, []);

  /* AUTO SELECT OPERATOR */
  useEffect(() => {
    if (selectedOperatorId && operators.length) {
      const op = operators?.find(o => o.operatorId === selectedOperatorId);
      setSelectedOperator(op);
      console.log("get the data",op)
    }

  }, [selectedOperatorId, operators]);

  /* SUBMIT REG + OPERATOR */
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!selectedOperatorId) return notify("Select FASTag Operator", "error");
    if (!reg) return notify("Vehicle Number required", "error");

    setSearchParams({ reg, operator: selectedOperatorId });
  };

  /* FETCH FASTAG BILL */
  const fetchFastag = async (regNo, opId) => {
    if (getLoading) return; // FIXED
    setGetLoading(true);

    try {
      const payload = {
        mode: "online",
        canumber: regNo,
        operatorId: opId,
      };

      const result = await fetch_fastag_info(payload);

      if (result?.success && result?.data?.bill) {
        notify("FASTag fetched successfully", "success");
        setDetails(result.data.bill);
        setShowDetails(true);
      } else {
        notify(result?.message || "Bill not found", "error");
        clearSession();
      }
    } catch (err) {
      console.error(err);
      notify("Server error", "error");
      clearSession();
    } finally {
      setGetLoading(false);
    }
  };

  /* URL WATCHER */
  useEffect(() => {
    const regNo = searchParams.get("reg");
    const opId = searchParams.get("operator");

    if (!regNo || !opId) return;
    if (fetchedRef.current === regNo) return;

    fetchedRef.current = regNo;
    setReg(regNo);
    setSelectedOperatorId(opId);

    fetchFastag(regNo, opId);
  }, [searchParams]);

  /* PAYMENT */
  const handlePay = async (data) => {
    if (!details || !reg) return notify("Missing fields", "error");
    if (!data.amount) return notify("Amount required", "error");

    setLoading(true);
    try {
      const location = await getLocation();

      const payload = {
        amount: data.amount,
        operatorId: selectedOperatorId,
        canumber: reg,
        latitude: location.latitude,
        longitude: location.longitude,
        category: slug,
        bill_fetch: data?.bill_fetch || details?.bill_fetch,
      };

      const result = await submit_fastag_amount(payload);

      if (result?.success) {
        notify("Payment Successful", "success");
        clearSession();
      } else {
        notify(result?.message || "Payment failed", "error");
      }
    } catch (err) {
      console.error(err);
      notify("Server error", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Main showRightPanel={false}>
      <AppBreadcrumbs
        items={[
          { label: "Dashboard", to: "/" },
          { label: "Recharge & Bills" },
          { label: "FASTag" },
        ]}
      />

      <div className="flex flex-col lg:flex-row gap-6 mt-4">
        {/* LEFT PANEL */}
        <div className="ds-card p-4 w-full max-w-[260px]">
          <h2 className="font-semibold mb-4">FASTag Recharge</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <label className="text-sm font-medium">Select FASTag Operator</label>
            <Autocomplete
              options={operators}
              getOptionLabel={(op) => op.name || ""}
              value={selectedOperator}
              onChange={(e, val) => {
                setSelectedOperator(val);
                setSelectedOperatorId(val?.operatorId || "");
              }}
              renderInput={(params) => (
                <TextField {...params} size="small" placeholder="Search Operator" />
              )}
            />


               <FormControl fullWidth className="mb-4" required>
<FormLabel required sx={{
  "& .MuiFormLabel-asterisk": { color: "red" }
}}>
  Vehicle Registration Number
</FormLabel>

            <input
              type="text"
              placeholder="HR2026AD"
              value={reg}
              onChange={(e) => setReg(e.target.value.toUpperCase())}
              className="w-full border rounded p-2"
            />

            </FormControl>

            <button
              type="submit"
              disabled={getLoading}
              className="ds-btn ds-btn-primary ds-btn-block"
            >
              {getLoading ? "Fetching..." : "Check FASTag"}
            </button>
          </form>
        </div>

        {/* RIGHT PANEL */}
        {showDetails && details && (
          <div className="w-full max-w-sm">
            <ServicesForm
              bill={details}
              loading={loading}
              onSubmit={handlePay}
              close={clearSession}
            />
          </div>
        )}
      </div>
    </Main>
  );
};

export default Fastag;
